import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Loader2, ArrowRight, LayoutDashboard, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import PlanCarousel from "@/components/PlanCarousel";
import YearlySavings from "@/components/YearlySavings";
import TrustStrip from "@/components/TrustStrip";
import { getSessionToken } from "@/lib/auth";
import PlanFeatureList from "@/components/PlanFeatureList";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const ORDER = ["free", "creator", "starter", "growth", "pro"];

const PLAN_FEATURES = {
  free: [
    "5 Catalog Products",
    "Sell Digital Products (Instant Delivery Link inside DMs and Emails) & Physical Products",
    "15 Customer Conversations / Month",
    "Full DM Checkout Automation on Instagram",
    "QR Code Payments, UPI ID, Bank Transfer (Manual Payment Confirmation)",
    "Razorpay, PayPal, Stripe (Automatic Confirmation)",
    "Unlimited Payment Links",
    "Automatic Story Replies (Generic Message)",
    "Basic Merchant Dashboard Access",
    "FAQs, Exchange Policy & Return Policy Shown Automatically in DMs",
    "Automatic Return & Exchange Handling",
    "Order & Return Email Confirmations for Customers",
    "Charge Shipping Fee From Customers at Checkout",
    "Shipped Emails (Sent When You Mark An Order Shipped or Book a Courier)",
    "Multiple Courier Partners Supported",
    "Schedule Courier Pickup & Reverse Pickup from Merchant Dashboard",
    "New Order Alerts on Merchant Dashboard",
    "Checkout DM Branding Shown to Customers in Emails & inside DMs",
    "Email Support",
  ],
    creator: [
    "150 Digital-Only Catalog Products",
    "Give Free Digital Products Also (Give Away Files or Links)",
    "Sell Digital Products Only (No Physical Products Allowed)",
    "Unlimited Conversations",
    "Comment-to-DM Automation",
    "Category based search inside DMs",
    "One-Link Checkout — Share it on YouTube, WhatsApp, TikTok, or anywhere; customers can buy without ever DMing you",
    "Preview / Affiliate Link Button on Any Product inside DMs",
    "Earn Money From Affiliate Products (Keep 100% Commission)",
    "Shareable Public Catalog Page (Storefront) for Your Digital Products",
    "Checkout DM Branding Inside Emails and Storefront",
    "Buyer Receives the Download Links in the Emails",
    "Merchants get the Customer Name & Emails",
    "Full Order Dashboard",
    "Email Support",
  ],
  starter: [
    "Everything in Free & Creator",
    "500 Catalog Products (Both Physical & Digital Items)",
    "Shareable Public Catalog Page with instant product search and 1 Extra Product Photo (2 Total)",
    "No Checkout DM Branding, Anywhere",
    "Customer Order History Dashboard",
    "Abandoned Cart Info to Merchant",
    "Full Merchant Dashboard",
    "Take Over Any Chat, Anytime",
    "Automatic Delivery Status Updates (Delivered / RTO / Failed, etc)",
    "Auto Stock Updates on Every Sale, Return or Exchange",
    "Catalog Search with 3 Filters/Keywords inside DMs",
    "Reminders for Unshipped Orders",
    "Automatic Free Shipping Above a Minimum Cart Value",
    "Your Company Address in Emails",
    "Fast Email Support",
  ],
  growth: [
    "Everything in Starter",
    "1,000 Catalog Products",
    "Social Links, Active Coupons, 3 More Extra Photo (3 Total) & More on Your Public Catalog Page",
    "Product-Matched Story Replies",
    "Dead Stock Insights",
    "Automatic 'Trending Products' Category Inside DMs",
    "Catalog Search with 4 Filters/Keywords inside DMs",
    "CSV Export of Customers, Orders & Returns",
    "Automatic & Manual Coupons (Applicable Once Per Customer)",
    "Time Based Offers (Auto Expire)",
    "Usage or Count Based Offers (Auto Expire After N Uses)",
    "Multi-Step Automatic Abandoned Cart Recovery",
    "Automatic Delivery Status Double-Check (Catches Any Missed Updates)",
    "Automatic Low Stock Alerts (Per Size)",
    "Automatically Ask For a Review From Customers After N Days",
    "Your Online Store's Social Media Links (Instagram, Facebook & YouTube) in Emails",
    "Revenue & Conversion Analytics",
    "Priority Support",
  ],
  pro: [
    "Everything in Growth",
    "10,000 Catalog Products",
    "3 Extra Product Photos (4 Total), Search Engine-Indexable Product Pages, Bestseller Products, Your Brand Logo & More on Your Public Catalog Page",
    "3 Team Members (1 Admin + 2 Users)",
    "Extra Team Members Available",
    "Catalog Search with 5 Filters/Keywords inside DMs",
    "Export Your Full Catalog to CSV",
    "Bulk Product Photo Upload (Upto 50 At Once)",
    "Quantity-Based Discounts",
    "Cross-Sell Suggestions In Order Emails",
    "Shuffle Catalog Order to Move Dead Stock in DMs",
    "Show Bestseller Products inside DMs to Customers",
    "A 1:1 Quick Onboarding Call with Founder Prince Jindal",
    "Real-Time Webhooks (Orders & Returns)",
    "Advanced Analytics",
    "Show Your Brand's Logo, Website, Phone & much more inside customer emails",
    "Dedicated Support",
  ],
};

const PLAN_DESCRIPTIONS = {
  free: "Try Checkout DM without paying a buck - no credit card required.",
  creator: "For digital creators selling files, guides, or freebies.",
  starter: "For full-time creators ready to sell every day, but have outgrown Free.",
  growth: "Reach inside the DMs of all your commenters and scale.",
  pro: "For brands running Instagram like a storefront, with a team behind it.",
};

export default function Plans() {
  const [plans, setPlans] = useState(null);
  const [me, setMe] = useState(null);
  const [yearly, setYearly] = useState(false);
  const [busy, setBusy] = useState("");
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [confirmPlan, setConfirmPlan] = useState(null); // { key, direction }
  const [couponCode, setCouponCode] = useState("");
  const [couponPreview, setCouponPreview] = useState(null); // { status: 'loading'|'valid'|'invalid', ... }
  const [couponWarning, setCouponWarning] = useState(null); // { message, resolve }
  const [emailGate, setEmailGate] = useState(null); // { planKey, couponCodeInput } — set when payment is blocked on a missing email
  const [gateEmail, setGateEmail] = useState("");
  const [gateEmailError, setGateEmailError] = useState("");
  const [gateBusy, setGateBusy] = useState(false);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const submitEmailGate = async () => {
    const email = gateEmail.trim();
    if (!EMAIL_RE.test(email)) {
      setGateEmailError("Enter a valid email address.");
      return;
    }
    setGateBusy(true);
    setGateEmailError("");
    try {
      await api.post("/merchant/contact-email", { contact_email: email });
      setMe((prev) => ({ ...(prev || {}), contact_email: email }));
      const pending = emailGate;
      setEmailGate(null);
      if (pending?.planKey === "free") handleFree(email);
      else if (pending) handlePaid(pending.planKey, pending.couponCodeInput, email);
    } catch (e) {
      setGateEmailError(e?.response?.data?.error === "invalid_email"
        ? "Enter a valid email address."
        : "Couldn't save that email. Try again.");
    } finally {
      setGateBusy(false);
    }
  };

  // Promise-based stand-in for window.confirm(), so the "coupon didn't
  // apply" prompt can be a styled modal instead of a native browser dialog.
  const askCouponWarning = (message) =>
    new Promise((resolve) => {
      setCouponWarning({ message, resolve });
    });

  // Debounced live preview: quotes the discounted price as the merchant
  // types a coupon code, WITHOUT reserving a redemption slot (that only
  // happens for real at /plan/razorpay/order, once they actually check out).
  useEffect(() => {
    if (!confirmPlan || confirmPlan.direction === "blocked" || confirmPlan.direction === "to_free") {
      setCouponPreview(null);
      return;
    }
    const code = couponCode.trim();
    if (!code) {
      setCouponPreview(null);
      return;
    }
    setCouponPreview({ status: "loading" });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/plan/coupon-preview", {
          plan: confirmPlan.key,
          billing_cycle: confirmPlan.cycle || (yearly ? "yearly" : "monthly"),
          coupon_code: code,
        });
        setCouponPreview({
          status: data.coupon_valid ? "valid" : "invalid",
          discountPercent: data.discount_percent,
          originalAmount: data.original_amount,
          discountedAmount: data.discounted_amount,
          currency: data.currency,
          message: data.coupon_message,
        });
      } catch {
        setCouponPreview({ status: "invalid", message: "Couldn't check that code — try again." });
      }
    }, 500);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponCode, confirmPlan?.key, confirmPlan?.cycle]);

  useEffect(() => {
    if (!getSessionToken()) {
      navigate("/login", { replace: true });
      return;
    }
    (async () => {
      try {
        const [cfg, meResp] = await Promise.all([
          api.get("/config"),
          api.get("/me"),
        ]);
        setPlans(cfg.data.plans);
        setMe(meResp.data);
      } catch {
        toast.error("Please sign in again.");
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate]);

  const cards = useMemo(() => {
    return ORDER.map((k) => ({ key: k, ...(plans?.[k] || {}) }));
  }, [plans]);

  const handleDowngrade = async (planKey) => {
    if (!window.confirm(`Switch to ${planKey.charAt(0).toUpperCase() + planKey.slice(1)}? This takes effect when your current plan expires — you keep your current features until then.`)) return;
    setBusy(planKey);
    try {
      await api.post("/plan/downgrade", { plan: planKey });
      toast.success(`You'll move to ${planKey.charAt(0).toUpperCase() + planKey.slice(1)} at your next renewal.`);
    } catch {
      toast.error("Couldn't schedule the downgrade. Try again.");
    } finally {
      setBusy("");
    }
  };

  const handleFree = async (emailOverride) => {
    // Same rule as paid plans: we need an email on file before this
    // merchant's account is fully set up, regardless of which plan they pick.
    if (!emailOverride && !me?.contact_email) {
      setGateEmail("");
      setGateEmailError("");
      setEmailGate({ planKey: "free", couponCodeInput: undefined });
      return;
    }
    setBusy("free");
    try {
      await api.post("/plan/select-free", { plan: "free" });
      toast.success("You're on the Free plan.");
      navigate("/dashboard");
    } catch (e) {
      if (e?.response?.data?.error === "email_required") {
        setGateEmail("");
        setGateEmailError("");
        setEmailGate({ planKey: "free", couponCodeInput: undefined });
      } else {
        toast.error("Could not activate the Free plan. Try again.");
      }
    } finally {
      setBusy("");
    }
  };

  const handlePaid = async (planKey, couponCodeInput, emailOverride) => {
    // Razorpay checkout requires an email on file — used for the invoice,
    // renewal reminders, and so support can reach the merchant directly.
    if (!emailOverride && !me?.contact_email) {
      setGateEmail("");
      setGateEmailError("");
      setEmailGate({ planKey, couponCodeInput });
      return;
    }
    setBusy(planKey);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Razorpay failed to load");
      const trimmedCoupon = (couponCodeInput || "").trim().toUpperCase();
      const { data } = await api.post("/plan/razorpay/order", {
        plan: planKey,
        billing_cycle: yearly ? "yearly" : "monthly",
        ...(trimmedCoupon ? { coupon_code: trimmedCoupon } : {}),
      });

      // Safety net for the gap between the live preview and the moment this
      // order is actually created: someone else may have grabbed the last
      // coupon slot, or this business may have already used it. The backend
      // is the source of truth here (it re-validates from scratch), so if it
      // silently didn't apply a coupon the merchant typed, stop and ask
      // before opening Razorpay at full price rather than surprising them
      // after they've already paid.
      if (trimmedCoupon && !data.coupon_applied) {
        const proceedFullPrice = await askCouponWarning(
          `The coupon "${trimmedCoupon}" couldn't be applied — it may have just run out, expired, or already been used on this account. You'll be charged the full price instead.`
        );
        setCouponWarning(null);
        if (!proceedFullPrice) {
          setBusy("");
          return;
        }
      }

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "Checkout DM",
        description: `${data.plan_name} ${yearly ? "Yearly" : "Monthly"}`,
        order_id: data.order_id,
        prefill: data.prefill,
        theme: { color: "#FF3366" },
        handler: async (resp) => {
          try {
            await api.post("/plan/razorpay/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              plan: planKey,
              billing_cycle: yearly ? "yearly" : "monthly",
            });
            toast.success(`${data.plan_name} plan activated!`);
            navigate("/dashboard");
          } catch {
            // The backend may have actually completed even though this
            // specific request errored or timed out client-side — check
            // before showing a scary "contact support" message.
            try {
              const { data: freshProfile } = await api.get("/profile");
              if (freshProfile.plan === planKey) {
                toast.success(`${data.plan_name} plan activated!`);
                navigate("/dashboard");
                return;
              }
            } catch {}
            toast.error("Payment went through but we couldn't confirm it here. Check your Billing page — if your plan hasn't updated in a minute, contact support.");
          }
        },
        modal: {
          ondismiss: () => setBusy(""),
        },
      };
      const rp = new window.Razorpay(options);
      rp.on("payment.failed", () => {
        toast.error("Payment failed. Please try again.");
        setBusy("");
      });
      rp.open();
    } catch (e) {
      if (e?.response?.data?.error === "email_required") {
        setGateEmail("");
        setGateEmailError("");
        setEmailGate({ planKey, couponCodeInput });
      } else {
        toast.error(e?.response?.data?.message || "Could not start checkout. Try again.");
      }
      setBusy("");
    }
  };

  const isIndia = (me?.country || "IN").toUpperCase() === "IN";
  // A merchant's dashboard is reachable once they've actually made a plan
  // choice (free or paid) — not merely because `me.plan` defaults to "free"
  // internally before that choice happens.
  const hasPlan = !!me?.plan_selected;

  return (
    <div className="min-h-screen bg-cream pt-16 pb-28 md:py-24">
      <div className="mx-auto max-w-6xl px-6 md:px-12">

        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl"
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">
            {me?.plan_selected ? "Manage your plan" : "Complete your account setup"}
            {me?.username ? ` · @${me.username}` : ""}
          </p>
          <h1
            className="mt-3 font-display text-4xl leading-[1.05] tracking-tighter md:text-6xl"
            data-testid="plans-title"
          >
            {me?.plan_selected ? (
              <>
                Upgrade, downgrade, or switch
                <br />
                billing cycles,
                <br />
                <span className="text-sunset">whenever you want.</span>
              </>
            ) : (
              <>
                Choose a plan and start
                <br />
                selling through Instagram,
                <br />
                <span className="text-sunset">in less than 5 minutes.</span>
              </>
            )}
          </h1>
        </motion.div>

        {/* Billing toggle */}
        <div className="mt-10 flex justify-center">
          <div className="relative inline-flex rounded-full border border-ink/10 bg-white p-1 shadow-sm">
            <button
              onClick={() => setYearly(false)}
              className={`relative z-0 rounded-full px-6 py-2.5 text-sm font-semibold transition-colors ${
                !yearly ? "text-white" : "text-ink/60"
              }`}
            >
              {!yearly && (
                <motion.span
                  layoutId="billing-toggle-pill"
                  transition={{ type: "spring", stiffness: 400, damping: 32 }}
                  className="absolute inset-0 -z-10 rounded-full bg-hotpink"
                />
              )}
              Monthly
            </button>
            <button
              onClick={() => setYearly(true)}
              className={`relative z-0 flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold transition-colors ${
                yearly ? "text-white" : "text-ink/60"
              }`}
            >
              {yearly && (
                <motion.span
                  layoutId="billing-toggle-pill"
                  transition={{ type: "spring", stiffness: 400, damping: 32 }}
                  className="absolute inset-0 -z-10 rounded-full bg-hotpink"
                />
              )}
              Yearly
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                  yearly ? "bg-white/20 text-white" : "bg-green-100 text-green-700"
                }`}
              >
                Save 2 Months
              </span>
            </button>
          </div>
        </div>

        <TrustStrip className="mb-10 mt-6" />

        {/* Cards */}
        <PlanCarousel labels={ORDER.map((k) => k.charAt(0).toUpperCase() + k.slice(1))} initialIndex={3}>
          {cards.map((p, i) => {
            const isHighlight = p.key === "growth";
            const isPro = p.key === "pro";
            const isCurrent =
            me?.plan_selected &&
            me?.plan === p.key &&
            (p.key === "free" ||
              (me?.billing_cycle || "monthly") === (yearly ? "yearly" : "monthly"));
            const active = busy === p.key;
            const monthlyPrice =
              p.price_inr ?? 0;

            const yearlyPrice =
              p.price_inr_yearly ?? 0;

            const monthlyUSD =
              p.price_usd ?? 0;

            const yearlyUSD =
              p.price_usd_yearly ?? 0;

            const price =
              yearly ? yearlyPrice : monthlyPrice;

            const usdPrice =
              yearly ? yearlyUSD : monthlyUSD;

            return (
              <motion.div
                key={p.key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06, duration: 0.4 }}
                whileHover={{ y: -6 }}
                data-testid={`plan-card-${p.key}`}
                className={`relative flex w-[82%] shrink-0 snap-center flex-col rounded-[2rem] border p-7 transition-all duration-300 sm:w-[58%] md:w-[340px] lg:w-[360px] ${
                  isHighlight
                    ? "border-transparent bg-sunset-gradient text-white shadow-[0_25px_60px_-20px_rgba(255,51,102,.45)] scale-[1.03]"
                    : isPro
                    ? "border-violet-500 bg-gradient-to-br from-violet-50 to-white shadow-lg"
                    : "border-ink/10 bg-white hover:shadow-xl"
                }`}
              >
                {isHighlight && (
                  <div className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs font-bold uppercase tracking-[0.15em] text-hotpink shadow">
                    <Sparkles size={12} />
                    Recommended
                  </div>
                )}

                {isPro && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-full bg-violet-600 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white shadow">
                    For brands &amp; teams
                  </div>
                )}

                <p className="font-display text-2xl font-black tracking-tight">
                  {p.name || p.key.charAt(0).toUpperCase() + p.key.slice(1)}
                </p>
                <p className={`mt-1 text-sm ${isHighlight ? "text-white/85" : "text-ink/60"}`}>
                  {PLAN_DESCRIPTIONS[p.key]}
                </p>

                <div className="mt-6 text-center">
                    {plans === null ? (
                      <div className={`mx-auto h-12 w-32 animate-pulse rounded-lg ${isHighlight ? "bg-white/20" : "bg-ink/10"}`} />
                    ) : (
                      <p className="font-display text-5xl font-black tracking-tighter">
                        {isIndia
                          ? `₹${Number(price || 0).toLocaleString("en-IN")}`
                          : `$${Number(usdPrice || 0)}`}
                      </p>
                    )}

                    <p className={`text-xs mt-1 ${isHighlight ? "text-white/80" : "text-ink/60"}`}>
                    {p.key === "free"
                      ? "Forever Free"
                      : yearly
                      ? "Billed yearly (Pay for 10 months)"
                      : "Billed monthly"}
                  </p>
                  {yearly && p.key !== "free" && (
                    <>
                      <p
                        className={`mt-1 text-xs font-semibold ${
                          isHighlight ? "text-white" : "text-hotpink"
                        }`}
                      >
                        2 Months Free
                      </p>
                      <YearlySavings
                        monthly={isIndia ? monthlyPrice : monthlyUSD}
                        yearly={isIndia ? yearlyPrice : yearlyUSD}
                        currency={isIndia ? "INR" : "USD"}
                        highlight={isHighlight}
                      />
                    </>
                  )}
                </div>

                <PlanFeatureList
                  features={PLAN_FEATURES[p.key]}
                  expanded={showAllFeatures}
                  onToggle={() => setShowAllFeatures((v) => !v)}
                  highlight={isHighlight}
                  testId={`plan-showmore-${p.key}`}
                />

                <div className="mt-auto pt-7">
                  {isCurrent && p.key !== "free" ? (
                    <motion.button
                      whileTap={{ scale: 0.97 }}
                      disabled={busy === p.key}
                      onClick={() => handlePaid(p.key)}
                      data-testid={`plan-current-${p.key}`}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-ink/15 bg-white px-5 py-3 text-sm font-semibold text-ink hover:bg-ink/5 disabled:opacity-70"
                    >
                      {busy === p.key ? <Loader2 size={16} className="animate-spin" /> : null}
                      Renew now
                    </motion.button>
                  ) : isCurrent ? (
                    <button
                      disabled
                      data-testid={`plan-current-${p.key}`}
                      className="w-full rounded-full border border-ink/15 bg-ink/5 px-5 py-3 text-sm font-semibold text-ink/60"
                    >
                      Current Plan
                    </button>
                  ) : (
                    <motion.button
                      whileTap={{ scale: 0.97 }}
                      disabled={active}
                      onClick={() => {
                        setCouponCode("");
                        if (p.key === "free") {
                          // Only warn about losing paid features when this is an actual
                          // downgrade from a plan they'd already chosen. For a brand-new
                          // merchant picking Free as their first choice, just activate it.
                          if (me?.plan_selected && me?.plan !== "free") {
                            setConfirmPlan({ key: "free", direction: "to_free" });
                          } else {
                            handleFree();
                          }
                          return;
                        }
                        const curPlan = me?.plan || "free";
                        const curCycle = me?.billing_cycle || "monthly";
                        const targetCycle = yearly ? "yearly" : "monthly";
                        const curRank = ORDER.indexOf(curPlan);
                        const targetRank = ORDER.indexOf(p.key);
                        const tierChanged = p.key !== curPlan;
                        const cycleChanged = curPlan !== "free" && targetCycle !== curCycle;

                        let direction;
                        if (curPlan === "free") direction = "new";
                        else if (tierChanged && cycleChanged) direction = "blocked";
                        else if (cycleChanged) direction = "cycle_change";
                        else if (targetRank > curRank) direction = "upgrade";
                        else direction = "downgrade";

                        setConfirmPlan({
                          key: p.key,
                          direction,
                          cycle: targetCycle,
                          displayPrice: isIndia ? `₹${Number(price || 0).toLocaleString("en-IN")}` : `$${Number(usdPrice || 0)}`,
                        });
                      }}
                      data-testid={`plan-cta-${p.key}`}
                      className={`inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition-colors ${
                        isHighlight
                          ? "bg-white text-ink hover:bg-white/90"
                          : "bg-ink text-cream hover:bg-ink/85"
                      } disabled:opacity-70`}
                    >
                      {active ? <Loader2 size={16} className="animate-spin" /> : null}
                      {p.key === "free"
                        ? "Start Free"
                        : p.key === "creator"
                        ? "Get Creator"
                        : p.key === "starter"
                        ? "Get Starter"
                        : p.key === "growth"
                        ? "Start Growing"
                        : "Go Pro"}
                      {!active && <ArrowRight size={16} />}
                    </motion.button>
                  )}
                  <p
                    className={`mt-3 text-center text-xs ${isHighlight ? "text-white/80" : "text-ink/50"}`}
                  >
                    {p.key === "free"
                      ? "No card required · Upgrade anytime"
                      : "No transaction fees · Cancel anytime"}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </PlanCarousel>

        <p className="mt-6 text-center text-xs text-ink/55">
          Subscriptions are non-refundable. Cancel anytime and keep access until your paid period ends.{" "}
          <Link to="/refund-policy" target="_blank" className="underline">
            Refund Policy
          </Link>
        </p>

        {/* Go to dashboard */}
        <div className="mt-10 hidden justify-center md:flex">
          <button
            type="button"
            onClick={() => hasPlan && navigate("/dashboard")}
            disabled={!hasPlan}
            title={hasPlan ? "Go to your dashboard" : "Choose a plan to unlock your dashboard"}
            data-testid="plans-go-to-dashboard"
            className={`inline-flex items-center gap-2 rounded-full border px-6 py-3 text-sm font-semibold transition-colors ${
              hasPlan
                ? "border-ink/15 bg-white text-ink hover:bg-ink/5"
                : "cursor-not-allowed border-ink/10 bg-ink/5 text-ink/35"
            }`}
          >
            <LayoutDashboard size={16} />
            Go to Dashboard
          </button>
        </div>

        {/* Go to dashboard — floating mobile CTA */}
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-ink/10 bg-cream/95 px-6 pt-3 pb-[calc(1rem+env(safe-area-inset-bottom))] backdrop-blur md:hidden">
          <button
            type="button"
            onClick={() => hasPlan && navigate("/dashboard")}
            disabled={!hasPlan}
            title={hasPlan ? "Go to your dashboard" : "Choose a plan to unlock your dashboard"}
            data-testid="plans-go-to-dashboard-mobile"
            className={`inline-flex w-full items-center justify-center gap-2 rounded-full border px-6 py-3 text-sm font-semibold shadow-lg transition-colors ${
              hasPlan
                ? "border-ink/15 bg-white text-ink hover:bg-ink/5"
                : "cursor-not-allowed border-ink/10 bg-ink/5 text-ink/35"
            }`}
          >
            <LayoutDashboard size={16} />
            Go to Dashboard
          </button>
        </div>

        {/* Footer */}
        <div className="mt-20 grid gap-6 md:grid-cols-3">

        <div className="rounded-3xl border p-6 bg-white">
          <h3 className="font-bold text-lg">
            No Transaction Commission
          </h3>
          <p className="mt-2 text-sm text-ink/70">
            CheckoutDM never takes any percentage from your sales.
          </p>
        </div>

        <div className="rounded-3xl border p-6 bg-white">
          <h3 className="font-bold text-lg">
            Payments Go Directly To You
          </h3>
          <p className="mt-2 text-sm text-ink/70">
            Customers pay directly into your Razorpay, Stripe, PayPal, UPI, QR Code or Bank Account.
          </p>
        </div>

        <div className="rounded-3xl border p-6 bg-white">
          <h3 className="font-bold text-lg">
            Cancel Anytime
          </h3>
          <p className="mt-2 text-sm text-ink/70">
            Upgrade, downgrade or cancel whenever you want. Payments already
            made aren't refunded, but you keep access until your paid period ends.
          </p>
        </div>

      </div>
      </div>

      {confirmPlan && (
          <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 backdrop-blur-sm sm:items-center sm:p-6">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              className="w-full max-w-sm rounded-t-[2rem] bg-white p-6 sm:rounded-[2rem]"
            >
              <h3 className="font-display text-xl font-black tracking-tight text-ink">
                {confirmPlan.direction === "to_free" ? "Switch to Free?" : confirmPlan.direction === "blocked" ? "Change one thing at a time" : "Confirm plan change"}
              </h3>
              <p className="mt-3 text-sm text-ink/70">
                {confirmPlan.direction === "to_free"
                  ? "This takes effect immediately — you'll lose your current plan's features right away, with no refund for unused days. If you're not sure, consider waiting until closer to your renewal date."
                  : confirmPlan.direction === "blocked"
                  ? `You can't change tier and billing cycle in the same step. Switch your billing cycle to ${confirmPlan.cycle === "yearly" ? "yearly" : "monthly"} first (keeping your current tier), then change tier separately.`
                  : confirmPlan.direction === "new"
                  ? `You'll be charged ${confirmPlan.displayPrice} now for ${confirmPlan.cycle === "yearly" ? "a full year" : "the first month"}. This payment is non-refundable — you can cancel anytime and keep access until the period ends.`
                  : confirmPlan.direction === "upgrade"
                  ? `You'll be charged a prorated amount now (full price is ${confirmPlan.displayPrice}/${confirmPlan.cycle === "yearly" ? "yr" : "mo"}), and the change takes effect immediately with a fresh billing cycle starting today. This payment is non-refundable.`
                  : confirmPlan.direction === "cycle_change"
                  ? `You'll be charged ${confirmPlan.displayPrice} now in full (no proration) — but it won't start yet. You'll keep your current plan and features until your current billing period ends, then switch over automatically. This payment is non-refundable.`
                  : `You'll be charged ${confirmPlan.displayPrice} now in full (no proration) — but it won't start yet. You'll keep your current plan and features until your current billing period ends, then switch over automatically. This payment is non-refundable.`}
              </p>

              {confirmPlan.direction !== "blocked" && confirmPlan.direction !== "to_free" && (
                <div className="mt-4">
                  <label htmlFor="coupon-code-input" className="mb-1.5 block text-xs font-semibold text-ink/60">
                    Coupon code (optional)
                  </label>
                  <input
                    id="coupon-code-input"
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="e.g. EARLY50"
                    autoCapitalize="characters"
                    data-testid="plans-coupon-input"
                    className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm font-medium tracking-wide text-ink placeholder:text-ink/30 focus:border-hotpink focus:outline-none"
                  />
                  {couponPreview?.status === "loading" && (
                    <p className="mt-2 text-xs text-ink/40">Checking code…</p>
                  )}
                  {couponPreview?.status === "invalid" && (
                    <p className="mt-2 text-xs font-medium text-red-500">
                      {couponPreview.message || "That code isn't valid."}
                    </p>
                  )}
                  {couponPreview?.status === "valid" && (
                    <p className="mt-2 text-xs font-medium text-green-600" data-testid="plans-coupon-valid">
                      {couponPreview.discountPercent}% off applied — you'll pay{" "}
                      <span className="line-through opacity-50">
                        {couponPreview.currency === "INR"
                          ? `₹${(couponPreview.originalAmount / 100).toLocaleString("en-IN")}`
                          : `$${(couponPreview.originalAmount / 100).toFixed(2)}`}
                      </span>{" "}
                      {couponPreview.currency === "INR"
                        ? `₹${(couponPreview.discountedAmount / 100).toLocaleString("en-IN")}`
                        : `$${(couponPreview.discountedAmount / 100).toFixed(2)}`}{" "}
                      instead.
                    </p>
                  )}
                </div>
              )}

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setConfirmPlan(null)}
                  className="flex-1 rounded-full border border-ink/15 bg-white px-5 py-3 text-sm font-semibold text-ink hover:bg-ink/5"
                >
                  {confirmPlan.direction === "blocked" ? "Got it" : "No, go back"}
                </button>
                {confirmPlan.direction !== "blocked" && (
                  <button
                    onClick={() => {
                      const plan = confirmPlan.key;
                      const coupon = couponCode;
                      setConfirmPlan(null);
                      plan === "free" ? handleFree() : handlePaid(plan, coupon);
                    }}
                    className="flex-1 rounded-full bg-ink px-5 py-3 text-sm font-semibold text-cream hover:bg-ink/85"
                  >
                    Yes, continue
                  </button>
                )}
              </div>
            </motion.div>
          </div>
        )}

      {couponWarning && (
          <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 backdrop-blur-sm sm:items-center sm:p-6">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              className="w-full max-w-sm rounded-t-[2rem] bg-white p-6 sm:rounded-[2rem]"
            >
              <h3 className="font-display text-xl font-black tracking-tight text-ink">
                Coupon couldn't be applied
              </h3>
              <p className="mt-3 text-sm text-ink/70">{couponWarning.message}</p>
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => couponWarning.resolve(false)}
                  data-testid="coupon-warning-cancel"
                  className="flex-1 rounded-full border border-ink/15 bg-white px-5 py-3 text-sm font-semibold text-ink hover:bg-ink/5"
                >
                  Cancel
                </button>
                <button
                  onClick={() => couponWarning.resolve(true)}
                  data-testid="coupon-warning-continue"
                  className="flex-1 rounded-full bg-ink px-5 py-3 text-sm font-semibold text-cream hover:bg-ink/85"
                >
                  Continue at full price
                </button>
              </div>
            </motion.div>
          </div>
        )}

      {emailGate && (
          <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 backdrop-blur-sm sm:items-center sm:p-6">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              className="w-full max-w-sm rounded-t-[2rem] bg-white p-6 sm:rounded-[2rem]"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-hotpink/10 text-hotpink">
                <Mail size={18} />
              </div>
              <h3 className="mt-4 font-display text-xl font-black tracking-tight text-ink">
                Add your email to continue
              </h3>
              <p className="mt-3 text-sm text-ink/70">
                We'll use this for your account and plan updates, renewal reminders, and so customers/support can reach you directly.
              </p>

              <div className="mt-4">
                <input
                  type="email"
                  autoFocus
                  value={gateEmail}
                  onChange={(e) => {
                    setGateEmail(e.target.value);
                    if (gateEmailError) setGateEmailError("");
                  }}
                  onKeyDown={(e) => e.key === "Enter" && !gateBusy && submitEmailGate()}
                  placeholder="you@example.com"
                  data-testid="plans-email-gate-input"
                  className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm font-medium text-ink placeholder:text-ink/30 focus:border-hotpink focus:outline-none"
                />
                {gateEmailError && (
                  <p className="mt-2 text-xs font-medium text-red-500" data-testid="plans-email-gate-error">
                    {gateEmailError}
                  </p>
                )}
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setEmailGate(null)}
                  disabled={gateBusy}
                  className="flex-1 rounded-full border border-ink/15 bg-white px-5 py-3 text-sm font-semibold text-ink hover:bg-ink/5 disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  onClick={submitEmailGate}
                  disabled={gateBusy}
                  data-testid="plans-email-gate-continue"
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-semibold text-cream hover:bg-ink/85 disabled:opacity-70"
                >
                  {gateBusy ? <Loader2 size={16} className="animate-spin" /> : null}
                  Continue
                </button>
              </div>
            </motion.div>
          </div>
        )}

    </div>
  );
}
